import { useState, useEffect, useRef, useCallback } from 'react';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import {
  Play, Pause, RotateCcw, Volume2, VolumeX, Minus, Plus, Timer, Zap
} from 'lucide-react';

const TICK_FREQ = 1000;
const TICK_DURATION = 50;
const TICK_VOLUME = 0.6;
const BEEP_VOLUME = 1.0;
const PHASE_CHANGE_FREQ = 400;
const PHASE_CHANGE_DURATION = 500;
const COUNTDOWN_FREQ = 600;
const COUNTDOWN_DURATION = 150;
const END_FREQ = 1000;
const END_DURATION = 800;
const START_FREQ = 800;
const START_DURATION = 300;

export default function TimerPage({ embedded = false }) {
  const [workSec, setWorkSec] = useState(60);
  const [restSec, setRestSec] = useState(60);
  const [rounds, setRounds] = useState(3);
  const [phase, setPhase] = useState('idle');
  const [currentRound, setCurrentRound] = useState(1);
  const [timeLeft, setTimeLeft] = useState(60);
  const [soundOn, setSoundOn] = useState(true);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Wake Lock — prevent screen sleep when timer is running
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch { /* Wake Lock not supported or denied */ }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  // Re-acquire wake lock if page becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && (phase === 'work' || phase === 'rest')) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [phase, requestWakeLock]);

  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (iOS requires user gesture)
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const playSound = useCallback((freq, duration, volume) => {
    if (!soundOn) return;
    try {
      const ctx = getAudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration / 1000);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration / 1000);
    } catch { /* Web Audio unavailable */ }
  }, [soundOn, getAudioCtx]);

  const playTick = useCallback(() => {
    playSound(TICK_FREQ, TICK_DURATION, TICK_VOLUME);
  }, [playSound]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => { stopInterval(); releaseWakeLock(); };
  }, [stopInterval, releaseWakeLock]);

  useEffect(() => {
    if (phase === 'idle' || phase === 'done') {
      stopInterval();
      releaseWakeLock();
      return;
    }

    // Acquire wake lock when running
    requestWakeLock();

    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (phase === 'work') {
            playSound(PHASE_CHANGE_FREQ, PHASE_CHANGE_DURATION, BEEP_VOLUME);
            setPhase('rest');
            return restSec;
          } else if (phase === 'rest') {
            if (currentRound >= rounds) {
              playSound(END_FREQ, END_DURATION, BEEP_VOLUME);
              setPhase('done');
              return 0;
            }
            playSound(START_FREQ, START_DURATION, BEEP_VOLUME);
            setCurrentRound(r => r + 1);
            setPhase('work');
            return workSec;
          }
        }
        // Countdown beeps for last 3 seconds
        if (prev <= 4 && prev > 1) {
          playSound(COUNTDOWN_FREQ, COUNTDOWN_DURATION, BEEP_VOLUME);
        } else {
          // Normal tick every second
          playTick();
        }
        return prev - 1;
      });
    }, 1000);

    return () => stopInterval();
  }, [phase, currentRound, rounds, workSec, restSec, playSound, playTick, stopInterval, requestWakeLock, releaseWakeLock]);

  const start = () => {
    // Resume audio context on user gesture (required for iOS)
    getAudioCtx();
    setCurrentRound(1);
    setTimeLeft(workSec);
    setPhase('work');
    playSound(START_FREQ, START_DURATION, BEEP_VOLUME);
  };

  const togglePause = () => {
    if (phase === 'work' || phase === 'rest') {
      stopInterval();
      releaseWakeLock();
      setPhase('idle');
    }
  };

  const reset = () => {
    stopInterval();
    releaseWakeLock();
    setPhase('idle');
    setCurrentRound(1);
    setTimeLeft(workSec);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const isRunning = phase === 'work' || phase === 'rest';

  const progressPct = phase === 'work'
    ? ((workSec - timeLeft) / workSec) * 100
    : phase === 'rest'
    ? ((restSec - timeLeft) / restSec) * 100
    : 0;

  const phaseColor = phase === 'work' ? '#ef4444' : phase === 'rest' ? '#10b981' : '#FF6B35';

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} bg-[#0F0F0F] ${embedded ? 'pb-4' : 'pb-20'} flex flex-col`}>
      {!embedded && (
      <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-[#FF6B35]" />
            <h1 className="text-xl font-bold text-white">Minuteur</h1>
          </div>
          <button onClick={() => setSoundOn(!soundOn)} className="p-2 text-zinc-500 hover:text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
            {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>
      </header>
      )}

      {/* Sound toggle when embedded */}
      {embedded && (
        <div className="flex justify-end px-4 pt-2">
          <button onClick={() => setSoundOn(!soundOn)} className="p-2 text-zinc-500 hover:text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
            {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>
      )}

      <main className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Config — only when idle */}
        {phase === 'idle' && !isRunning && (
          <div className="w-full max-w-sm space-y-4 mb-8" data-testid="timer-config">
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4">
              <p className="text-xs text-red-400 font-medium mb-2 flex items-center gap-1"><Zap className="w-3 h-3" /> TRAVAIL</p>
              <div className="flex items-center justify-between">
                <button onClick={() => setWorkSec(Math.max(10, workSec - 10))} className="w-12 h-12 rounded-sm bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 active:bg-zinc-600">
                  <Minus className="w-5 h-5" />
                </button>
                <p className="font-mono text-3xl font-bold text-white">{fmt(workSec)}</p>
                <button onClick={() => setWorkSec(workSec + 10)} className="w-12 h-12 rounded-sm bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 active:bg-zinc-600">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4">
              <p className="text-xs text-green-400 font-medium mb-2 flex items-center gap-1"><Pause className="w-3 h-3" /> REPOS</p>
              <div className="flex items-center justify-between">
                <button onClick={() => setRestSec(Math.max(10, restSec - 10))} className="w-12 h-12 rounded-sm bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 active:bg-zinc-600">
                  <Minus className="w-5 h-5" />
                </button>
                <p className="font-mono text-3xl font-bold text-white">{fmt(restSec)}</p>
                <button onClick={() => setRestSec(restSec + 10)} className="w-12 h-12 rounded-sm bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 active:bg-zinc-600">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4">
              <p className="text-xs text-[#FF6B35] font-medium mb-2">ROUNDS</p>
              <div className="flex items-center justify-between">
                <button onClick={() => setRounds(Math.max(1, rounds - 1))} className="w-12 h-12 rounded-sm bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 active:bg-zinc-600">
                  <Minus className="w-5 h-5" />
                </button>
                <p className="font-mono text-3xl font-bold text-white">{rounds}</p>
                <button onClick={() => setRounds(rounds + 1)} className="w-12 h-12 rounded-sm bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 active:bg-zinc-600">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Phase indicator when running */}
        {(isRunning || phase === 'done') && (
          <div className="mb-4">
            <p className="text-zinc-500 text-sm text-center mb-1">Round {currentRound}/{rounds}</p>
            <p className={`text-2xl font-bold text-center mb-6 ${
              phase === 'work' ? 'text-red-400 animate-pulse' : phase === 'rest' ? 'text-green-400' : 'text-[#FF6B35]'
            }`}>
              {phase === 'work' ? 'TRAVAIL !' : phase === 'rest' ? 'REPOS' : 'TERMINE !'}
            </p>
          </div>
        )}

        {/* Circle */}
        <div className="relative w-64 h-64 mb-8">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#1a1a24" strokeWidth="5" />
            {(isRunning || phase === 'done') && (
              <circle
                cx="50" cy="50" r="45" fill="none"
                stroke={phaseColor}
                strokeWidth="5"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - progressPct / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-1000"
              />
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-mono text-6xl font-bold text-white">
              {isRunning || phase === 'done' ? fmt(timeLeft) : fmt(workSec)}
            </span>
            {phase === 'idle' && <span className="text-xs text-zinc-500 mt-2">Pret ?</span>}
          </div>
        </div>

        {/* Controls */}
        <div className="flex gap-4">
          {phase === 'idle' && (
            <Button onClick={start} className="bg-[#FF6B35] text-black hover:bg-[#E85A25] px-10 py-6 text-lg font-bold" data-testid="start-timer">
              <Play className="w-6 h-6 mr-2" /> GO !
            </Button>
          )}
          {isRunning && (
            <>
              <Button onClick={togglePause} variant="outline" className="border-zinc-600 text-zinc-300 px-6 py-6">
                <Pause className="w-6 h-6 mr-2" /> Pause
              </Button>
              <Button onClick={reset} variant="outline" className="border-zinc-600 text-zinc-300 px-6 py-6">
                <RotateCcw className="w-6 h-6 mr-2" /> Reset
              </Button>
            </>
          )}
          {phase === 'done' && (
            <Button onClick={reset} className="bg-[#FF6B35] text-black hover:bg-[#E85A25] px-10 py-6 text-lg font-bold">
              <RotateCcw className="w-6 h-6 mr-2" /> Recommencer
            </Button>
          )}
        </div>

        {/* Wake Lock indicator */}
        {isRunning && (
          <p className="text-xs text-zinc-400 mt-4">Ecran actif — le telephone ne se mettra pas en veille</p>
        )}
      </main>

      {!embedded && <BottomNav />}
    </div>
  );
}
